import { darken } from "polished";
import styled, { css } from "styled-components";

type ButtonProps = {
  hasIcon?: boolean;
  isSubmitting?: boolean;
  hasSubmittingMessage?: boolean;
  bgColor?: string;
  bordercolor?: string;
  color?: string;
  height?: string;
};

export const Icon = styled.span`
  font-size: 0;
  line-height: 0;
  transition: all 0.25s ease;

  transform: translate3d(-30px, 0px, 0px);
  visibility: hidden;
  opacity: 0;
  margin-right: 0.625rem;
`;

export const Text = styled.span`
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  line-height: 1.5rem;
  margin-bottom: -2px;

  transition: all 0.25s ease;
`;

export const TextSubmitting = styled.span`
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  font-size: 1rem;

  transition: all 0.25s ease;
`;

export const LoadingIcon = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "hasText",
})<{ hasText?: boolean }>`
  display: block;

  width: 1rem;
  height: 1rem;
  border: 0.125rem solid var(--white);
  border-radius: 50%;
  animation: loadingAnimation 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: var(--white) transparent transparent transparent;

  margin-right: ${(props) => (props.hasText ? "0.625rem" : "0")};

  @keyframes loadingAnimation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const Button = styled.button.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "hasIcon",
      "isSubmitting",
      "hasSubmittingMessage",
      "bgColor",
      "bordercolor",
      "color",
      "height",
    ].includes(prop),
})<ButtonProps>`
  background: ${(props) => darken(0.1, props?.bgColor || "#F6C76B")};
  color: ${(props) => props?.color || "#2F2F2F"};
  border: 1px solid ${(props) => darken(0.1, props?.bordercolor || "#F6C76B")};
  border-radius: 2px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.75rem;
  height: ${(props) => props?.height || "3.125rem"};
  position: relative;
  font-size: 0;
  line-height: 0;

  transition: all 0.25s;

  ${Icon} {
    display: ${(props) => (props?.hasIcon ? "block" : "")};
  }

  ${Text} {
    transform: ${(props) =>
      props?.hasIcon ? "translate3d(-4.5px, 0px, 0px)" : "unset"};

    @media print, screen and (min-width: 40em) {
      transform: ${(props) =>
        props?.hasIcon ? "translate3d(-14.5px, 0px, 0px)" : "unset"};
    }

    color: ${(props) => props?.color || "#2F2F2F"};
  }

  &:hover {
    background: ${(props) => darken(0.2, props?.bgColor || "#F6C76B")};
    border-color: ${(props) => darken(0.2, props?.bordercolor || "#F6C76B")};

    ${Icon} {
      opacity: 1;
      visibility: visible;
      transform: translate3d(0px, 0px, 0px);
    }

    ${Text} {
      transform: ${(props) =>
        props?.hasIcon ? "translate3d(-5px, 0px, 0px)" : "unset"};
    }
  }

  ${Text} {
    ${(props) =>
      props.isSubmitting &&
      !props.hasSubmittingMessage &&
      css`
        transform: unset;
        opacity: 0;
      `}
  }

  ${LoadingIcon} {
    ${(props) =>
      props.isSubmitting &&
      !props.hasSubmittingMessage &&
      css`
        display: flex;
        -webkit-box-align: center;
        align-items: center;
        position: absolute;
      `}
  }
`;
